import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useUser } from '../../../contexts/UserContext';
import { useSavedSnapshots } from '../../../hooks/useSavedSnapshots';
import { useWeekCalculation } from '../../../hooks/useWeekSelection';
import { formatWeekKey, getCurrentISOWeekKey, getWeekStartDateFromKey, getNextWeekKey, getMonthYearLabelForWeekKey } from '../../../utils/weekKeyUtils';
import { testId } from '../../../utils/testId';
import { useSaveStatus } from '../../../contexts/SaveStatusContext';
// import { smartPrefetch } from '../../../utils/lazyLoaders'; // Unused for now (type mismatch with tab keys)
import { useWeekCache } from '../../../hooks/useWeekCache';
import { logger } from '../../../utils/logger';
import { trackProductEvent } from '../../../utils/productAnalytics';

// Import logo image for proper bundling
import BubbleLogo from '../../../assets/Group-1.png';

interface NavBarProps {
  activeTab: string;
  onTabChange: (tab: string) => void | Promise<void>;
}

/**
 * Tab configuration for navigation bar
 * Includes prefetch keys for performance optimization
 */
// ✅ SINGLE CANONICAL SOURCE: Tab order for ALL nav render paths (desktop + mobile)
// Order: Updates → Notes → Spend → Codes
// Spend is visible to all (viewers see it read-only to track their spend)
type TabConfig = {
  key: string;
  label: string;
  testId: string;
  prefetchKey: string;
  adminOnly?: boolean;
};

const TAB_CONFIG: TabConfig[] = [
  { key: 'updates', label: 'Updates', testId: 'updates-tab', prefetchKey: 'updates' },
  { key: 'notes', label: 'Notes', testId: 'notes-tab', prefetchKey: 'notes' },
  { key: 'spend', label: 'Spend', testId: 'spend-tab', prefetchKey: 'spend' },
  // NOTE: Keep this key aligned with `TabType` + `renderTabContent` in `src/App.tsx`
  { key: 'promo-codes', label: 'Codes', testId: 'codes-tab', prefetchKey: 'codes' },
];

const TAB_BASE_CLASS = 'tab-base';
const TAB_ACTIVE_CLASS = 'tab-active';
const TAB_DEFAULT_CLASS = 'tab-default';

const NavBar: React.FC<NavBarProps> = ({ activeTab, onTabChange }) => {
  const { canEdit, isActuallyAdmin, role, viewerMode, workspaceId } = useUser();
  const { selectedWeek, setWeek, dbWeekKeys, refreshDbWeekKeys } = useWeekCalculation();
  const { deleteSnapshot, switchToWeek } = useSavedSnapshots();
  const { isSaving, lastSavedAt } = useSaveStatus();

  // ✅ PART 1: DEV-only log tab order once on mount (via logger)
  useEffect(() => {
    if (import.meta.env.DEV) {
      const renderedKeys = TAB_CONFIG
        .filter(tab => !tab.adminOnly || canEdit)
        .map(tab => tab.key);
      logger.debug('[NavOrder]', { renderedKeys }, 'NavBar');
    }
  }, [canEdit]);
  
  // Week cache for prefetching (Phase 2)
  const { prefetchAdjacentWeeks } = useWeekCache(workspaceId);
  
  const canEditAnySection = canEdit;
  const analyticsRole = viewerMode ? 'viewer_mode' : (isActuallyAdmin ? 'admin' : (role ?? 'viewer'));
  
  const currentWeek = getCurrentISOWeekKey(new Date());
  
  // MIN_WEEK_KEY: Hardcoded to 2025-W48 (Week of 11/24/2025) - when we first began
  // This ensures the week dropdown only shows weeks from our start date onwards
  const MIN_WEEK_KEY = '2025-W48';
  
  const [showWeekSelector, setShowWeekSelector] = useState(false);
  const [showAllWeeks, setShowAllWeeks] = useState(false);
  // ✅ FIX 2: Viewport detection to prevent both mobile and desktop dropdowns from rendering
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  
  /**
   * Week tick state - triggers recalculation every Monday at 00:00
   * Ensures week labels update correctly at week boundaries
   */
  const [weekTick, setWeekTick] = useState<number>(() => Date.now());

  const nextWeekKey = useMemo(() => {
    return getNextWeekKey(currentWeek);
  }, [currentWeek, weekTick]);

  // ✅ ARCHITECTURAL FIX: dbWeekKeys now managed by WeekProvider (single source of truth)
  // NavBar consumes it from context instead of managing its own state
  // refreshDbWeekKeys is still called locally for delete operations

  // ✅ FIX 2: Handle viewport resize for mobile/desktop detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const now = new Date();
    const day = now.getDay();
    const daysUntilMonday = (1 - day + 7) % 7;
    const nextMonday = new Date(now);
    nextMonday.setDate(now.getDate() + daysUntilMonday);
    nextMonday.setHours(0, 0, 0, 0);
    const msUntilNextMonday = nextMonday.getTime() - now.getTime();
    
    let intervalId: number | null = null;
    const timeoutId = setTimeout(() => {
      setWeekTick(Date.now());
      intervalId = setInterval(() => setWeekTick(Date.now()), 7 * 24 * 60 * 60 * 1000) as unknown as number;
      window.__calendarWeeklyIntervalId = intervalId;
    }, Math.max(msUntilNextMonday, 0));

    return () => {
      clearTimeout(timeoutId as unknown as number);
      // Clean up interval if it was created
      if (intervalId) {
        clearInterval(intervalId);
      }
      // Also clean up global reference
      const id = window.__calendarWeeklyIntervalId;
      if (id) {
        clearInterval(id);
        window.__calendarWeeklyIntervalId = undefined;
      }
    };
  }, []);

  /**
   * Generate week lists for dropdown selector
   * 
   * VIEWER FILTERING RULES (Client-Safe UX):
   * - Viewers ONLY see: past weeks with real DB data
   * - Admins see: all weeks (past, current, future)
   * 
   * This prevents viewers from:
   * - Seeing deleted weeks (no DB rows)
   * - Seeing empty weeks (no DB rows)
   * - Seeing future weeks (not yet finalized)
   * - Seeing current in-progress week (week < currentWeek filter)
   */
  const { recentWeeks, olderWeeks, weekFlags } = useMemo(() => {
    // Build DB weeks set (for hasDbRows flag)
    const dbWeeksSet = new Set(dbWeekKeys);
    
    // ✅ FIX: Build timeline range from MIN_WEEK_KEY forward to max(currentWeek, latestDBWeek, current+2weeks)
    // Always show at least current ± 2 weeks in collapsed view (matching recent weeks logic)
    const latestDBWeek = dbWeekKeys.length > 0 ? dbWeekKeys.sort()[dbWeekKeys.length - 1] : currentWeek;
    const currentPlusTwoWeeks = getNextWeekKey(getNextWeekKey(currentWeek)); // current + 2 weeks
    const maxWeek = [latestDBWeek, currentWeek, currentPlusTwoWeeks].sort()[2]; // max of all three
    
    // Generate timeline: ALL weeks from MIN_WEEK_KEY forward to maxWeek
    // This ensures deleted weeks remain visible in dropdown
    const timelineWeeks: string[] = [];
    let weekKey = MIN_WEEK_KEY;
    const seenWeeks = new Set<string>();
    let iterations = 0;
    const maxIterations = 100; // Safety limit (should never hit)
    
    // Generate all weeks from MIN_WEEK_KEY forward to maxWeek
    while (weekKey <= maxWeek && iterations < maxIterations) {
      // Safety check to prevent infinite loop
      if (seenWeeks.has(weekKey)) break;
      seenWeeks.add(weekKey);
      iterations++;
      
      timelineWeeks.push(weekKey);
      
      // Move to next week
      const nextWeek = getNextWeekKey(weekKey);
      // Stop if we've exceeded maxWeek or if nextWeek calculation fails
      if (nextWeek === weekKey || nextWeek > maxWeek) break;
      weekKey = nextWeek;
    }
    
    // Also include any DB weeks outside the timeline range (edge case)
    const allWeeksSet = new Set<string>([...timelineWeeks, ...dbWeekKeys]);
    
    // Always include selected week and current week (for UX continuity)
    allWeeksSet.add(selectedWeek);
    allWeeksSet.add(currentWeek);
    
    // ✅ PART 3: Compute flags for each week (hasDbRows, label suffix)
    const flags = new Map<string, { hasDbRows: boolean }>();
    for (const week of allWeeksSet) {
      if (week && week >= MIN_WEEK_KEY) {
        flags.set(week, {
          hasDbRows: dbWeeksSet.has(week)
        });
      }
    }
    
    // Categorize weeks into recent (≤14 days) and older
    const recentWeeksSet = new Set<string>();
    const olderWeeksSet = new Set<string>();
    
    for (const week of allWeeksSet) {
      if (!week || week < MIN_WEEK_KEY) continue;
      
      const weekDate = getWeekStartDateFromKey(week);
      const currentDate = getWeekStartDateFromKey(currentWeek);
      if (weekDate && currentDate) {
        const diffDays = Math.abs((weekDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays <= 14) {
          recentWeeksSet.add(week);
        } else {
          olderWeeksSet.add(week);
        }
      } else {
        // Fallback: use timeline position
        if (timelineWeeks.indexOf(week) <= 4) {
          recentWeeksSet.add(week);
        } else {
          olderWeeksSet.add(week);
        }
      }
    }
    
    const sortWeeks = (weeks: string[]) => weeks.sort((a, b) => {
      const ad = getWeekStartDateFromKey(a);
      const bd = getWeekStartDateFromKey(b);
      if (!ad || !bd) return 0;
      return bd.getTime() - ad.getTime();
    });
    
    const sortedRecent = sortWeeks(Array.from(recentWeeksSet));
    const sortedOlder = sortWeeks(Array.from(olderWeeksSet));
    
    // ✅ VIEWER MODEL: Show same full week list as admin (no gaps). Viewers see all weeks;
    // "no data" weeks are shown but disabled in the dropdown (handled in render).
    // viewerMaxWeek is computed in WeekProvider for redirect logic only.
    
    if (import.meta.env.DEV) {
      const allWeeksArray = [...sortedRecent, ...sortedOlder];
      const checkWeeks = ['2025-W50', '2025-W51', '2025-W52', '2026-W01', '2026-W02'];
      const weekPresence = checkWeeks.reduce((acc, wk) => {
        acc[wk] = allWeeksArray.includes(wk);
        return acc;
      }, {} as Record<string, boolean>);
      
      logger.debug('[WeekDropdown]', {
        finalWeeksCount: allWeeksArray.length,
        dbWeekKeysCount: dbWeekKeys.length,
        timelineWeeksCount: timelineWeeks.length,
        recentWeeksCount: sortedRecent.length,
        olderWeeksCount: sortedOlder.length,
        maxWeek,
        latestDBWeek,
        selectedWeek,
        currentWeek,
        weekPresence,
        sampleWeeks: allWeeksArray.slice(0, 20)
      }, 'NavBar');
    }
    
    return {
      recentWeeks: sortedRecent,
      olderWeeks: sortedOlder,
      weekFlags: flags
    };
  }, [currentWeek, weekTick, MIN_WEEK_KEY, dbWeekKeys, selectedWeek]);

  /**
   * Group weeks by month for dropdown display
   * Same UI/UX for admins and viewers: current ± 2 weeks initially, "Show Older Weeks" to expand
   */
  const groupedWeeks = useMemo(() => {
    const allWeeks = showAllWeeks ? [...recentWeeks, ...olderWeeks] : recentWeeks;
    const grouped: Record<string, string[]> = {};
    
    for (const wk of allWeeks) {
      const label = getMonthYearLabelForWeekKey(wk);
      (grouped[label] ??= []).push(wk);
    }

    return { grouped, weekFlags, recentWeeks, olderWeeks };
  }, [recentWeeks, olderWeeks, weekFlags, selectedWeek, showAllWeeks]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [weekToDelete, setWeekToDelete] = useState<string>('');
  const weekSelectorRef = useRef<HTMLDivElement>(null);
  const weekSelectorButtonRef = useRef<HTMLButtonElement>(null);

  const handleWeekChange = useCallback((week: string) => {
    trackProductEvent('week_selected', {
      period_key: week,
      previous_period_key: selectedWeek,
      role: analyticsRole,
    });
    setWeek(week);
    setShowWeekSelector(false);
    switchToWeek(week);
  }, [analyticsRole, selectedWeek, setWeek, switchToWeek]);

  const handleWeekHover = useCallback((week: string) => {
    if (workspaceId) {
      prefetchAdjacentWeeks(week, workspaceId);
    }
  }, [prefetchAdjacentWeeks, workspaceId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        weekSelectorRef.current &&
        !weekSelectorRef.current.contains(event.target as Node) &&
        weekSelectorButtonRef.current &&
        !weekSelectorButtonRef.current.contains(event.target as Node)
      ) {
        setShowWeekSelector(false);
      }
    };

    if (showWeekSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showWeekSelector]);

  // Prefetch adjacent weeks when dropdown opens
  useEffect(() => {
    if (showWeekSelector && selectedWeek && workspaceId) {
      // prefetchAdjacentWeeks expects (currentWeek, section)
      prefetchAdjacentWeeks(selectedWeek, workspaceId);
    }
  }, [showWeekSelector, selectedWeek, workspaceId, prefetchAdjacentWeeks]);

  const handleWeekSelectorToggle = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const nextOpen = !showWeekSelector;
    setShowWeekSelector(nextOpen);
    if (nextOpen) {
      trackProductEvent('week_dropdown_opened', {
        period_key: selectedWeek,
        role: analyticsRole,
      });
    }
  };

  const handleDeleteClick = useCallback((week: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (import.meta.env.DEV) {
      logger.debug('[NavBar] Delete clicked', {
        week,
        weekFormatted: formatWeekKey(week),
        weekDate: getWeekStartDateFromKey(week)?.toISOString()
      }, 'NavBar');
    }
    
    setTimeout(() => {
      setWeekToDelete(week);
      setShowDeleteModal(true);
    }, 0);
  }, []);

  // Helper: Find nearest week from remaining DB weeks after delete
  const findNearestWeek = useCallback((deletedWeek: string, remainingWeeks: string[]): string | null => {
    if (remainingWeeks.length === 0) return currentWeek;
    
    const deletedDate = getWeekStartDateFromKey(deletedWeek);
    if (!deletedDate) return remainingWeeks[0] || currentWeek;
    
    // Sort remaining weeks by date
    const sorted = [...remainingWeeks]
      .map(wk => ({ week: wk, date: getWeekStartDateFromKey(wk) }))
      .filter(({ date }) => date !== null)
      .sort((a, b) => (b.date!.getTime() - a.date!.getTime())); // Descending
    
    // Find previous week (most recent before deleted)
    const previous = sorted.find(({ date }) => date && date.getTime() < deletedDate.getTime());
    if (previous) return previous.week;
    
    // Find next week (earliest after deleted)
    const next = sorted.find(({ date }) => date && date.getTime() > deletedDate.getTime());
    if (next) return next.week;
    
    // Fallback to first available or current week
    return remainingWeeks[0] || currentWeek;
  }, [currentWeek]);

  const handleConfirmDelete = async () => {
    if (!weekToDelete) return;

    const deletedWeek = weekToDelete;
    const wasSelectedWeek = weekToDelete === selectedWeek;

    // Close modal immediately so user isn't stuck if delete hangs or fails
    setShowDeleteModal(false);
    setWeekToDelete('');
    setShowWeekSelector(false);
    
    // ✅ CRITICAL FIX: Verify weekToDelete matches what user clicked
    // Log both values to catch any state corruption
    if (import.meta.env.DEV) {
      logger.debug('[NavBar] Delete confirmed, starting deletion', {
        weekToDelete: deletedWeek,
        weekFormatted: formatWeekKey(deletedWeek),
        weekDate: getWeekStartDateFromKey(deletedWeek)?.toISOString(),
        wasSelectedWeek,
        selectedWeek,
        weekToDeleteState: weekToDelete
      }, 'NavBar');
    }
    
    // ✅ CRITICAL FIX: Double-check weekToDelete hasn't changed (modal already closed above)
    if (weekToDelete !== deletedWeek) {
      logger.error('[NavBar] CRITICAL: weekToDelete changed during delete!', {
        original: deletedWeek,
        current: weekToDelete
      }, 'NavBar');
      return;
    }

    try {
      await deleteSnapshot(deletedWeek);
      
      // ✅ CRITICAL FIX: Track deleted week in BOTH DEV and PROD (prevent auto-persistence)
      // This MUST run in production to prevent forward-fill from recreating deleted weeks
      try {
        const raw = sessionStorage.getItem('marlo:deletedWeeks');
        const deletedWeeks = raw ? (JSON.parse(raw) as string[]) : [];
        if (!deletedWeeks.includes(deletedWeek)) {
          deletedWeeks.push(deletedWeek);
          sessionStorage.setItem('marlo:deletedWeeks', JSON.stringify(deletedWeeks));
        }
      } catch (e) {
        // Non-critical - if sessionStorage fails, forward-fill may recreate week
        // But this is better than crashing the delete operation
        if (import.meta.env.DEV) {
          logger.debug('Failed to track deleted week in sessionStorage', { error: e, deletedWeek }, 'NavBar');
        }
      }
      
      // ✅ CRITICAL FIX: Refresh DB week keys immediately after delete
      const updatedWeekKeys = await refreshDbWeekKeys();
      
      // ✅ VERIFICATION: DEV-only logging for delete verification
      if (import.meta.env.DEV) {
        logger.debug('[WeekDelete]', {
          deletedWeek,
          beforeDeleteCount: dbWeekKeys.length,
          afterDeleteCount: updatedWeekKeys.length,
          deletedWeekStillInDB: updatedWeekKeys.includes(deletedWeek),
          beforeDeleteWeeks: [...dbWeekKeys].sort(),
          afterDeleteWeeks: [...updatedWeekKeys].sort()
        }, 'NavBar');
      }
      
      // ✅ If deleted week was selected, navigate to nearest remaining week
      if (wasSelectedWeek && updatedWeekKeys) {
        const nearestWeek = findNearestWeek(deletedWeek, updatedWeekKeys);
        if (nearestWeek && nearestWeek !== deletedWeek) {
            if (import.meta.env.DEV) {
              logger.debug('[NavBar] Navigating to nearest week after delete', {
                from: deletedWeek,
                to: nearestWeek
              }, 'NavBar');
            }
          handleWeekChange(nearestWeek);
        } else {
          // No remaining weeks, navigate to current
          handleWeekChange(currentWeek);
        }
      }

      // ✅ PART 3: No window.location.reload() - optimistic UI update
      // Week remains in dropdown (timeline-based), just without trash icon
      // All useSimpleSnapshot hooks will detect rowExists=false on next access
    } catch (error) {
      logger.error('Failed to delete snapshot', { week: deletedWeek, error }, 'NavBar');
      // Modal already closed; user can retry from dropdown
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setWeekToDelete('');
  };

  /**
   * Keyboard shortcuts for delete modal
   * Enter: confirm deletion, Escape: cancel
   */
  useEffect(() => {
    if (!showDeleteModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirmDelete();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelDelete();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, weekToDelete]);


  const handleTabClick = useCallback(async (tabKey: string) => {
    const result = onTabChange(tabKey);
    if (result && typeof (result as Promise<void>).then === 'function') {
      await (result as Promise<void>);
    }
    // Prefetch adjacent weeks for the selected tab if applicable
    if (selectedWeek && workspaceId && (tabKey === 'updates' || tabKey === 'codes' || tabKey === 'spend')) {
      prefetchAdjacentWeeks(selectedWeek, workspaceId);
    }
  }, [onTabChange, workspaceId, selectedWeek, prefetchAdjacentWeeks]);

  const handleTabHover = useCallback(() => {
    // smartPrefetch expects specific LoaderKey types, skip for now if type mismatch
    // Prefetch handled in handleTabClick
  }, []);

  useEffect(() => {
    const updateCurrentWeek = () => {
      const currentWeekKey = getCurrentISOWeekKey();
      if (selectedWeek === currentWeekKey) {
        localStorage.setItem('dashboard-selected-week', selectedWeek);
      }
    };

    updateCurrentWeek();
    const interval = setInterval(updateCurrentWeek, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [selectedWeek]);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-40 bg-[var(--bg-elev)] border-b border-[var(--border)] backdrop-blur-md py-2 sm:py-3"
      style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
    >
      {/* Mobile Layout */}
      <div className="sm:hidden flex flex-col px-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold text-textPrimary">
            <img 
              src={BubbleLogo} 
              className="h-6 w-auto dark:invert dark:brightness-0 dark:contrast-200" 
              alt="Bubble"
              loading="eager"
            />
            <div className="relative">
              <button
                ref={weekSelectorButtonRef}
                data-test="mobile-week-selector"
                onMouseDown={handleWeekSelectorToggle}
                className="inline-flex items-center gap-1 text-accent hover:text-accentStrong 
                           underline decoration-[color-mix(in_srgb,var(--accent-primary)_30%,_transparent)] hover:decoration-[color-mix(in_srgb,var(--accent-primary)_50%,_transparent)] 
                           underline-offset-4 rounded px-1 py-0.5
                           transition-colors"
                aria-haspopup="listbox"
                aria-expanded={showWeekSelector}
              >
                {formatWeekKey(selectedWeek)}
                <ChevronDown size={16} className={`transition-transform ${showWeekSelector ? 'rotate-180' : ''}`} />
              </button>

              {/* ✅ FIX 2: Only render mobile dropdown on mobile viewports */}
              {isMobile && showWeekSelector && createPortal(
                (() => {
                  const triggerRect = weekSelectorButtonRef.current?.getBoundingClientRect();
                  return (
                    <div
                      ref={weekSelectorRef}
                      data-floating-ui-portal
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'fixed',
                        left: triggerRect ? triggerRect.left : 0,
                        top: triggerRect ? triggerRect.bottom + 8 : 0,
                        width: '280px',
                        maxWidth: '90vw',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        // CRITICAL: Force solid background - no transparency
                        backgroundColor: 'var(--bg-elev)',
                        backgroundImage: 'none',
                        border: '1px solid var(--border)',
                        borderRadius: '12px',
                        boxShadow: 'var(--shadow-strong)',
                        zIndex: 12000, // Higher than NavBar's z-40 (which is 40)
                        backdropFilter: 'none',
                        WebkitBackdropFilter: 'none',
                        transform: 'none',
                        opacity: 1,
                        isolation: 'isolate', // Create new stacking context to escape NavBar's backdrop-filter
                        // Additional safety: ensure no transparency
                        mixBlendMode: 'normal',
                      }}
                      className="week-selector-popper week-selector-dropdown"
                    >
                      <div className="p-3">
                        <div className="text-xs font-medium text-textMuted mb-2 px-2">Select Week</div>
                        {Object.entries(groupedWeeks.grouped).map(([month, weeks]) => (
                          <div key={month} className="mb-2">
                            <div className="text-xs font-semibold text-textMuted px-2 py-1 bg-[var(--bg-muted)] rounded-t border-b border-border">
                              {month}
                            </div>
                            {weeks.map((week) => {
                              // ✅ PART 3: Use weekFlags.hasDbRows for trash icon visibility and viewer clickability
                              const weekInfo = groupedWeeks.weekFlags.get(week);
                              const hasDbRows = weekInfo?.hasDbRows ?? false;
                              const isViewerNoData = !canEditAnySection && !hasDbRows;
                              return (
                                <div
                                  key={week}
                                  className={`flex items-center justify-between px-2 py-2 rounded text-sm transition-colors ${
                                    week === selectedWeek ? 'bg-tableHover text-textPrimary font-semibold' : 'hover:bg-tableHover text-textMuted'
                                  } ${isViewerNoData ? 'opacity-75' : ''}`}
                                >
                                  <button
                                    type="button"
                                    disabled={isViewerNoData}
                                    onClick={() => !isViewerNoData && handleWeekChange(week)}
                                    onMouseEnter={() => !isViewerNoData && handleWeekHover(week)}
                                    className={`flex-1 text-left rounded px-1 py-1.5 text-sm transition-colors text-textMuted hover:text-textPrimary ${isViewerNoData ? 'cursor-not-allowed' : 'hover:bg-tableHover'}`}
                                    title={isViewerNoData ? 'No data for this week' : undefined}
                                  >
                                    {formatWeekKey(week)}
                                    {!hasDbRows && <span className="text-xs text-textMuted ml-1">(No data)</span>}
                                    {week === currentWeek && <span className="ml-1 text-[11px] font-medium text-accent">(Current)</span>}
                                    {week === nextWeekKey && <span className="ml-1 text-[11px] font-medium text-textMuted">(Next)</span>}
                                  </button>
                                  {/* ✅ PART 3: Trash icon only when hasDbRows === true */}
                                  {canEditAnySection && week !== currentWeek && hasDbRows && (
                                    <button
                                      onClick={(e) => handleDeleteClick(week, e)}
                                      onMouseDown={(e) => e.preventDefault()}
                                      className="ml-2 p-2 text-textMuted hover:text-danger hover:bg-[color-mix(in_srgb,var(--color-danger) 10%, transparent)] rounded transition-colors focus:outline-none focus:ring-2 focus:ring-danger/50"
                                      title="Delete snapshot"
                                      aria-label="Delete snapshot"
                                      type="button"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                        <div className="border-t border-border pt-2 mt-2">
                          <button
                            onClick={() => setShowAllWeeks(!showAllWeeks)}
                            className="w-full text-left px-2 py-2 text-sm text-[var(--text)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 10%, var(--bg-elev) 90%)] rounded transition-colors"
                          >
                            {showAllWeeks ? 'Hide Older Weeks' : 'Show Older Weeks'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })(),
                document.body
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2">
          {TAB_CONFIG.filter(tab => !tab.adminOnly || canEditAnySection).map((tab) => (
            <motion.div key={tab.key} whileTap={{ scale: 0.95 }} {...testId(`mobile-${tab.testId}`)}>
              <button
                data-test={`mobile-${tab.testId}`}
                onClick={() => handleTabClick(tab.key)}
                onMouseEnter={() => handleTabHover()}
                className={`${TAB_BASE_CLASS} ${activeTab === tab.key ? TAB_ACTIVE_CLASS : TAB_DEFAULT_CLASS}`}
                tabIndex={0}
              >
                {tab.label}
              </button>
            </motion.div>
          ))}
        </div>

        {/* ✅ USER REQUEST: No "Saving..." indicator, only show "Saved at [time]" after actual changes */}
        {(() => {
          const timeSinceSave = lastSavedAt ? Date.now() - lastSavedAt : null;
          const shouldRender = lastSavedAt && timeSinceSave !== null && timeSinceSave < 2000;
          return shouldRender ? (
            <div className="text-xs text-center py-1">
              <span className="text-green-600">
                Saved at {new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            </div>
          ) : null;
        })()}
      </div>

      <div className="hidden sm:flex items-center justify-between px-6">
        <div className="flex items-center space-x-8">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 text-2xl lg:text-3xl font-semibold text-textPrimary"
          >
            <img 
              src={BubbleLogo} 
              className="h-7 lg:h-8 w-auto dark:invert dark:brightness-0 dark:contrast-200" 
              alt="Bubble"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="flex items-center gap-2">
              <span>Weekly Report w/o</span>
              <div className="relative">
                <button
                  ref={weekSelectorButtonRef}
                  data-test="desktop-week-selector"
                  onMouseDown={handleWeekSelectorToggle}
                  className="inline-flex items-center gap-1 text-accent hover:text-accentStrong 
                             underline decoration-[color-mix(in_srgb,var(--accent-primary)_30%,_transparent)] hover:decoration-[color-mix(in_srgb,var(--accent-primary)_50%,_transparent)] 
                             underline-offset-4 rounded px-1 py-0.5
                             transition-colors"
                  aria-haspopup="listbox"
                  aria-expanded={showWeekSelector}
                >
                  {formatWeekKey(selectedWeek)}
                  <ChevronDown size={18} className={`transition-transform ${showWeekSelector ? 'rotate-180' : ''}`} />
                </button>

                {/* ✅ FIX 2: Only render desktop dropdown on desktop viewports */}
                {!isMobile && showWeekSelector && weekSelectorButtonRef.current && createPortal(
                  <div
                    ref={weekSelectorRef}
                    data-floating-ui-portal
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'fixed',
                      left: weekSelectorButtonRef.current.getBoundingClientRect().left,
                      top: weekSelectorButtonRef.current.getBoundingClientRect().bottom + 8,
                      width: '240px',
                      maxWidth: '300px',
                      maxHeight: '80vh',
                      overflowY: 'auto',
                      // CRITICAL: Force solid background - no transparency
                      backgroundColor: 'var(--bg-elev)',
                      backgroundImage: 'none',
                      border: '1px solid var(--border)',
                      borderRadius: '12px',
                      boxShadow: 'var(--shadow-strong)',
                      zIndex: 12000, // Higher than NavBar's z-40 (which is 40)
                      backdropFilter: 'none',
                      WebkitBackdropFilter: 'none',
                      opacity: 1,
                      isolation: 'isolate', // Create new stacking context to escape NavBar's backdrop-filter
                      // Additional safety: ensure no transparency
                      mixBlendMode: 'normal',
                    }}
                    className="week-selector-popper week-selector-dropdown"
                  >
                    <div className="p-2">
                      <div className="text-xs font-medium text-textMuted mb-2 px-2">Select Week</div>
                      {Object.entries(groupedWeeks.grouped).map(([month, weeks]) => (
                        <div key={month} className="mb-2">
                          <div className="text-xs font-semibold text-textMuted px-2 py-1 bg-[var(--bg-muted)] rounded-t border-b border-border">
                            {month}
                          </div>
                          {weeks.map((week) => {
                            // ✅ PART 3: Use weekFlags.hasDbRows for trash icon visibility and viewer clickability
                            const weekInfo = groupedWeeks.weekFlags.get(week);
                            const hasDbRows = weekInfo?.hasDbRows ?? false;
                            const isViewerNoData = !canEditAnySection && !hasDbRows;
                            return (
                              <div
                                key={week}
                                className={`flex items-center justify-between px-2 py-1 rounded text-sm transition-colors ${
                                  week === selectedWeek ? 'bg-tableHover text-textPrimary font-semibold' : 'hover:bg-tableHover text-textMuted'
                                } ${isViewerNoData ? 'opacity-75' : ''}`}
                              >
                                <button
                                  type="button"
                                  disabled={isViewerNoData}
                                  onClick={() => !isViewerNoData && handleWeekChange(week)}
                                  onMouseEnter={() => !isViewerNoData && handleWeekHover(week)}
                                  className={`flex-1 text-left rounded px-1 py-1 text-sm transition-colors text-textMuted hover:text-textPrimary ${isViewerNoData ? 'cursor-not-allowed' : 'hover:bg-tableHover'}`}
                                  title={isViewerNoData ? 'No data for this week' : undefined}
                                >
                                  {formatWeekKey(week)}
                                  {!hasDbRows && <span className="text-xs text-textMuted ml-1">(No data)</span>}
                                  {week === currentWeek && <span className="ml-1 text-[11px] font-medium text-accent">(Current)</span>}
                                  {week === nextWeekKey && <span className="ml-1 text-[11px] font-medium text-textMuted">(Next)</span>}
                                </button>
                                {/* ✅ PART 3: Trash icon only when hasDbRows === true */}
                                {canEditAnySection && week !== currentWeek && hasDbRows && (
                                  <button
                                    onClick={(e) => handleDeleteClick(week, e)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="ml-2 p-1 text-textMuted hover:text-danger hover:bg-[color-mix(in_srgb,var(--color-danger) 10%, transparent)] rounded transition-colors focus:outline-none focus:ring-2 focus:ring-danger/50"
                                    title="Delete snapshot"
                                    type="button"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      <div className="border-t border-border pt-2 mt-2">
                        <button
                          onClick={() => setShowAllWeeks(!showAllWeeks)}
                          className="w-full text-left px-2 py-1 text-sm text-[var(--text)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 10%, var(--bg-elev) 90%)] rounded transition-colors"
                        >
                          {showAllWeeks ? 'Hide Older Weeks' : 'Show Older Weeks'}
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            </div>
          </motion.div>
        </div>

        <div className="flex items-center space-x-1 relative z-40">
          <div className="ml-auto mr-2 text-xs">
            {(() => {
              const timeSinceSave = lastSavedAt ? Date.now() - lastSavedAt : null;
              const shouldRender = !isSaving && lastSavedAt && timeSinceSave !== null && timeSinceSave < 3000;
              return isSaving ? (
                <span className="text-textMuted">Saving...</span>
              ) : shouldRender ? (
                <span className="text-green-600">
                  Saved at {new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              ) : null;
            })()}
          </div>
          
          {/* Desktop Tabs - Simple Buttons */}
          <div className="flex items-center gap-2 ml-4">
            {TAB_CONFIG.filter(tab => !tab.adminOnly || canEditAnySection).map((tab) => (
              <motion.div key={tab.key} whileTap={{ scale: 0.95 }} {...testId(`desktop-${tab.testId}`)}>
                <button
                  data-test={`desktop-${tab.testId}`}
                  onClick={() => handleTabClick(tab.key)}
                  onMouseEnter={() => handleTabHover()}
                  className={`${TAB_BASE_CLASS} ${activeTab === tab.key ? TAB_ACTIVE_CLASS : TAB_DEFAULT_CLASS}`}
                  tabIndex={0}
                >
                  {tab.label}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {showDeleteModal && createPortal(
        <div 
          className="fixed inset-0 z-[12000] flex items-center justify-center" 
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)'
          }}
          onClick={(e) => {
            // Close modal when clicking backdrop
            if (e.target === e.currentTarget) {
              handleCancelDelete();
            }
          }}
        >
          <div 
            className="card-surface rounded-lg shadow-elev2 p-6 w-full max-w-md relative mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              zIndex: 12001,
              pointerEvents: 'auto',
              backgroundColor: 'var(--bg-elev)',
              border: '1px solid var(--border)'
            }}
          >
            <button
              className="absolute top-2 right-2 text-textMuted hover:text-textPrimary p-1 rounded hover:bg-tableHover transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCancelDelete();
              }}
              aria-label="Close"
              type="button"
              style={{ pointerEvents: 'auto', zIndex: 12002 }}
            >
              ×
            </button>
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <Trash2 className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-semibold text-textPrimary">Delete Snapshot</h2>
              </div>
            </div>
            <p className="text-textMuted mb-6">
              Are you sure you want to delete the snapshot for{' '}
              <span className="font-semibold">{formatWeekKey(weekToDelete)}</span>?
              <br />
              <span className="text-sm text-textMuted">This action cannot be undone.</span>
            </p>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCancelDelete();
                }}
                className="px-4 py-2.5 text-sm font-medium text-textPrimary bg-card border border-border rounded-md hover:bg-tableHover transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2"
                type="button"
                style={{ pointerEvents: 'auto', zIndex: 12002 }}
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleConfirmDelete();
                }}
                className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 cursor-pointer"
                type="button"
                style={{ pointerEvents: 'auto', zIndex: 12002 }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </motion.nav>
  );
};

NavBar.displayName = 'NavBar';

export default NavBar;
